import { AiIngest } from "../../../api";
import { useAuth } from "../../../context";
import { useMutation, useQuery, useQueryClient } from "react-query";

// Both a dry run and a commit go through one mutation, because they are the same call with one flag
// different — and because the panel needs them to be provably the same payload. Two hooks would invite
// two payload-building paths, which is exactly the divergence this feature has to avoid.
//
// No `useQuery` variant: a dry run is not a cacheable read of a resource. It is an assertion about a
// payload at a moment in time, and serving it from cache after the frame changed would report a stale
// verdict as a current one.
export function useAiIngest(config) {
  const { callApi } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(
    ({ questionnaireId, request, dryRun }) =>
      callApi(AiIngest.ingest, { questionnaireId, request, dryRun }),
    {
      ...config,
      onSuccess: (data, variables, context) => {
        // A real commit changed the task's rows, so the preview's Existing view must stop showing the old
        // ones. A dry run wrote nothing and leaves the cache alone.
        if (!variables?.dryRun) queryClient.invalidateQueries(["aiIngestExistingRows", variables?.questionnaireId]);
        config?.onSuccess?.(data, variables, context);
      },
    }
  );
}

// The rows already in a target, one page at a time, for the commit preview. A read of a resource, so a
// query; invalidated by a real commit above. `keepPreviousData` so paging does not blank the table between
// pages.
export function useAiIngestExistingRows({ questionnaireId, target, page = 1, size = 100 }, config) {
  const { callApi } = useAuth();
  return useQuery(
    ["aiIngestExistingRows", questionnaireId, target, page, size],
    () => callApi(AiIngest.getExistingRows, { questionnaireId, target, page, size }),
    { enabled: Boolean(questionnaireId && target), keepPreviousData: true, refetchOnWindowFocus: false, ...config }
  );
}

// Two shapes of the same read, and the distinction is not cosmetic.
//
// The MUTATION is for "fetch it now because I am about to act on it" — reloading a stale descriptor before
// a commit, where serving a cached one would defeat the point.
export function useGetAiIngestTemplate(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ questionnaireId, target }) => callApi(AiIngest.getTemplate, { questionnaireId, target }),
    config
  );
}

// The QUERY is for display: the AI Mapping page's task bar shows which destination and which reporting
// period the session writes into. Cacheable, unlike a dry run, because it genuinely is a read of a
// resource — and it reads the descriptor rather than the data-sorting session, so the period is there
// immediately instead of only after the agent has called get_task_template.
//
// Disabled until both ids exist: a session can legitimately have no target yet, and firing the request
// without one returns the whole-task 400 rather than something displayable.
export function useAiIngestTemplateQuery({ questionnaireId, target }, config) {
  const { callApi } = useAuth();
  return useQuery(
    ["aiIngestTemplate", questionnaireId, target],
    () => callApi(AiIngest.getTemplate, { questionnaireId, target }),
    { enabled: Boolean(questionnaireId && target), ...config }
  );
}


// The ingest history for one task. A read of a resource, so a query — and cheap, because the server sends
// summaries. Disabled without an id so a task page that has not resolved yet does not fire it.
export function useAiIngestRuns(questionnaireId, config) {
  const { callApi } = useAuth();
  return useQuery(
    ["aiIngestRuns", questionnaireId],
    () => callApi(AiIngest.getRuns, { questionnaireId }),
    { enabled: Boolean(questionnaireId), ...config }
  );
}

// One run's full derivation. Fetched only when a user opens it: the methodology document and step log are
// the two unbounded fields on the record, so loading them for every run in the list would make the history
// cost scale with how much work the task has had done to it.
export function useAiIngestRun({ questionnaireId, runId }, config) {
  const { callApi } = useAuth();
  return useQuery(
    ["aiIngestRun", questionnaireId, runId],
    () => callApi(AiIngest.getRun, { questionnaireId, runId }),
    { enabled: Boolean(questionnaireId && runId), ...config }
  );
}
